import { GameStateActions } from "@/components/organisms/GameBoard";

import { ReactNode, useState, useEffect, useRef } from "react";
import { Box, Flex, Grid } from "styled-system/jsx";
import { button, input } from "styled-system/recipes";

import { mapDeckCuriosa } from "./mappers";
import { actShuffleDeck } from "@/utils/actions";
import { CuriosaResponse } from "@/utils/api/curiosa/api";
import { Tabs } from "@/components/atoms/Tabs";
import { UseQueryResult } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { getDeckQuery } from "@/components/organisms/GameBoard/useDeckQuery";
import { DefaultDecks } from "./DefaultDecks";
import LocalDeckLoader from "./LocalDeckLoader";
import DirectImport from "./DirectImport";
import PreconLoader from "./PreconLoader";
import { getCardImage } from "@/utils/getCardImage";


import type { Card, LocalDeck } from "@/components/organisms/DeckBuilder/types";
import {
  decodeDeck,
  isValidDeck,
  parseCuriosaDeckId,
  fetchCuriosaDeck,
} from "@/components/organisms/DeckBuilder/utils/deckEncoding";



export const LoadDeck = (
  props: GameStateActions & { children?: ReactNode; playerName: string },
) => {
  const [deckId, setDeckId] = useState<string>("");

  function setDeck(deck?: CuriosaResponse) {
    const newGrid = mapDeckCuriosa({
      deck,
      gridItems: props.gridItems,
      playerName: props.playerName,
    });
    if (!newGrid) return;
    const shuffledDeck = actShuffleDeck(newGrid, "deck");
    const shuffledAtlas = actShuffleDeck(shuffledDeck, "atlas");
    if (newGrid) props.setGridItems(shuffledAtlas);
    toast.success(`${props.playerName}'s deck was loaded`);
  }

  return (
    <Grid
      w="100vw"
      h="100vh"
      placeItems="center"
      bg="#28231fff"
      overflowX="hidden"
    >
      <Box
        position="relative"              // ✅ required
        w="100%"
        maxW={!!deckId ? "900px" : "600px"}
        m="0 auto"
        p="1rem"
        borderRadius="20px"
        overflow="hidden"               // ✅ keeps bg inside rounded corners
      >
        {/* Background image layer (rotated) */}
        <Box
          position="absolute"
          inset="0%"                   // ✅ avoids clipping after rotation
          bgImage="url('/bg/beetle.png')"
          bgSize="cover"
          bgPosition="center"
          transform="rotate(-0deg)"
          transformOrigin="center"
          opacity={0.8}
          zIndex={0}
          pointerEvents="none"
        />

        {/* Tint overlay */}
        <Box
          position="absolute"
          inset="0"
          bg="#ffffff"
          opacity={0.35}
          zIndex={1}
          pointerEvents="none"
        />

        {/* Content (must be above overlays) */}
        <Box position="relative" zIndex={2}>
          {props.children}
          <Box mt="0.5rem"></Box>
          <Tabs
            tabs={[
              "PreCons",
              "My Decks",
              "Import URL",
            ]}
            content={[
              <PreconLoader key="PreCons" setDeck={setDeck} />,
              <LocalDeckLoader key="MyDecks" setDeck={setDeck} />,
              <ImportURLLoader key="Import URL" setDeck={setDeck} />,
            ]}
          />
        </Box>
      </Box>
    </Grid>
  );

};

const ImportURLLoader = ({ setDeck }: { setDeck(deck?: CuriosaResponse): void }) => {
  const [text, setText] = useState("");
  const [error, setError] = useState<string>("");
  const [importing, setImporting] = useState(false);

  const [previewSlugs, setPreviewSlugs] = useState<string[]>([]);
  const [resolvedDeck, setResolvedDeck] = useState<CuriosaResponse | undefined>(
    undefined,
  );

  // ✅ cards index required for Curiosa import
  const [cardsIndex, setCardsIndex] = useState<Card[] | null>(null);

  // Cache the fetch so we don't start multiple loads / race
  const cardsPromiseRef = useRef<Promise<Card[]> | null>(null);

  const ensureCardsIndex = async (): Promise<Card[]> => {
    if (cardsIndex) return cardsIndex;

    if (!cardsPromiseRef.current) {
      cardsPromiseRef.current = (async () => {
        const res = await fetch("/card-data/processed_cards.json", {
          cache: "no-store",
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(
            `Card index fetch failed (${res.status}): ${txt.slice(0, 120)}`,
          );
        }
        return (await res.json()) as Card[];
      })();
    }

    const data = await cardsPromiseRef.current;
    setCardsIndex(data);
    return data;
  };

  // Start loading early (Preview will also await it if needed)
  useEffect(() => {
    ensureCardsIndex().catch((e) => {
      setError(e instanceof Error ? e.message : "Failed to load card index");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseDeckParamIfURL = (txt: string): string | null => {
    try {
      const url = new URL(txt);
      return url.searchParams.get("deck");
    } catch {
      return null;
    }
  };

  // LocalDeck-ish -> CuriosaResponse-ish (so mapDeckCuriosa keeps working)
  const toCuriosaResponse = (d: Partial<LocalDeck>): CuriosaResponse => {
    return {
      name: d.name ?? "Imported Deck",
      avatar: d.avatar ? [{ identifier: d.avatar, quantity: 1 }] : [],
      spellbook: (d.spellbook ?? []).map((s) => ({ identifier: s, quantity: 1 })),
      atlas: (d.atlas ?? []).map((s) => ({ identifier: s, quantity: 1 })),
    } as unknown as CuriosaResponse;
  };

  const handlePreview = async () => {
    const inputText = text.trim();
    if (!inputText) {
      setError("Please paste a deck link or code");
      return;
    }

    setImporting(true);
    setError("");
    setResolvedDeck(undefined);
    setPreviewSlugs([]);

    try {
      let localDeck: Partial<LocalDeck>;

      // 1) Curiosa URL / id
      const curiosaId = parseCuriosaDeckId(inputText);
      if (curiosaId) {
        const idx = await ensureCardsIndex();
        localDeck = await fetchCuriosaDeck(curiosaId, idx);
      } else {
        // 2) sorceryleivi URL with ?deck=
        const deckParam = inputText.includes("deck=")
          ? parseDeckParamIfURL(inputText)
          : null;

        // 3) raw code
        const encoded = deckParam ?? inputText;

        localDeck = decodeDeck(encoded);
      }

      if (!isValidDeck(localDeck)) throw new Error("Invalid deck format");

      setResolvedDeck(toCuriosaResponse(localDeck));

      // match LocalDeck preview behavior: unique cards only (spellbook+atlas), cap to 20
      const unique = Array.from(
        new Set([...(localDeck.spellbook ?? []), ...(localDeck.atlas ?? [])]),
      );
      setPreviewSlugs(unique);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to import deck");
    } finally {
      setImporting(false);
    }
  };

  const handleUse = () => {
    if (!resolvedDeck) return;
    setDeck(resolvedDeck);
  };

  const avatarSlug = resolvedDeck?.avatar?.[0]?.identifier;

  const totalCount =
    (resolvedDeck?.spellbook?.length ?? 0) + (resolvedDeck?.atlas?.length ?? 0);

  return (
    <Box>
      <textarea
        className={input()}
        placeholder="Paste a SorceryLeivi deck URL (?deck=...) or a Curiosa deck URL"
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{
          width: "100%",
          height: "96px",
          resize: "vertical",
          fontFamily: "monospace",
          fontSize: "0.875rem",
        }}
      />

      {error && (
        <Box
          mt="0.5rem"
          p="0.5rem"
          borderRadius="0.5rem"
          bg="rgba(255, 65, 65, 0.64)"
          border="1px solid rgba(167, 8, 8, 0.86)"
        >
          <p style={{ fontSize: "0.875rem", color: "white" }}>{error}</p>
        </Box>
      )}

      <Flex gap="0.5rem" mt="0.75rem">
        <button
          className={button()}
          onClick={handlePreview}
          disabled={importing || !text.trim()}
          style={{ flex: 1 }}
        >
          {importing ? "Importing..." : "Preview"}
        </button>

        <button
          className={button()}
          onClick={handleUse}
          disabled={!resolvedDeck}
          style={{ flex: 1 }}
        >
          Use this deck
        </button>
      </Flex>

      {/* ✅ Deck preview like LocalDeckLoader */}
      {!!resolvedDeck && (
        <Box mt="1rem">
          <h4 style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
            Deck Preview:
          </h4>

          <Grid
            gap="0.25rem"
            gridTemplateColumns="repeat(auto-fill, minmax(120px, 1fr))"
            maxH="200px"
            overflowY="auto"
            pr="0.25rem"
          >
            {avatarSlug && (
              <img
                src={getCardImage(avatarSlug)}
                alt="Avatar"
                style={{ width: "100%", borderRadius: "0.25rem" }}
              />
            )}

            {previewSlugs.slice(0, 20).map((slug, index) => (
              <img
                key={slug + index}
                src={getCardImage(slug)}
                alt={slug}
                style={{ width: "100%", borderRadius: "0.25rem" }}
              />
            ))}
          </Grid>

          {totalCount > 20 && (
            <p
              style={{
                fontSize: "0.75rem",
                color: "#9ca3af",
                textAlign: "center",
                marginTop: "0.5rem",
              }}
            >
              ... and {totalCount - 20} more cards
            </p>
          )}
        </Box>
      )}
    </Box>
  );
};
